// API Base URL - update this with your actual API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.dawaak-andna.com/';

export interface ApiCategory {
  id: number;
  name: {
    en: string;
    ar: string;
  };
  subcategories: ApiSubcategory[];
  createdAt: string;
  updatedAt: string;
}

export interface ApiSubcategory {
  id: number;
  name: {
    en: string;
    ar: string;
  };
  parentId?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiProduct {
  id: number;
  name: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  price: number;
  isActive: boolean;
  category: string;
  subcategory: string;
  images: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Fetch categories from the API
 */
export const fetchCategories = async (): Promise<ApiCategory[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}api/categories`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`);
    }
    
    const result: ApiResponse<ApiCategory[]> = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error('API returned unsuccessful response');
    }
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Fetch a single category with its subcategories from the API
 */
export const fetchCategoryById = async (id: number): Promise<ApiCategory> => {
  try {
    const response = await fetch(`${API_BASE_URL}api/categories/${id}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch category: ${response.statusText}`);
    }
    
    const result: ApiResponse<ApiCategory> = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error('API returned unsuccessful response');
    }
  } catch (error) {
    console.error(`Error fetching category ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch products by subcategory ID from the API
 */
export const fetchProductsBySubcategory = async (subcategoryId: number): Promise<ApiProduct[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}api/subcategories/${subcategoryId}/products`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.statusText}`);
    }
    
    const result: ApiResponse<ApiProduct[]> = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error('API returned unsuccessful response');
    }
  } catch (error) {
    console.error(`Error fetching products for subcategory ${subcategoryId}:`, error);
    throw error;
  }
};

/**
 * Fetch all products from the API
 */
export const fetchAllProducts = async (): Promise<ApiProduct[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}api/products`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.statusText}`);
    }
    
    const result: ApiResponse<ApiProduct[]> = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error('API returned unsuccessful response');
    }
  } catch (error) {
    console.error('Error fetching all products:', error);
    throw error;
  }
};
